'use client'

import { createContext, useCallback, useContext, useMemo, useState } from 'react'

type Toast = { id: string; message: string; kind: 'info' | 'error' | 'success' }

const ToastCtx = createContext<{ push: (message: string, kind?: Toast['kind']) => void } | null>(null)

function uuid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const push = useCallback((message: string, kind: Toast['kind'] = 'info') => {
    const id = uuid()
    setToasts(prev => [...prev, { id, message, kind }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500)
  }, [])

  const value = useMemo(() => ({ push }), [push])

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div style={{ position: 'fixed', top: 14, right: 14, zIndex: 60, display: 'grid', gap: 10, width: 'min(360px, 92vw)' }}>
        {toasts.map(t => (
          <div
            key={t.id}
            style={{
              borderRadius: 14,
              border: '1px solid var(--border)',
              background: 'var(--panel)',
              color: 'var(--text)',
              padding: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.25)'
            }}
          >
            <div style={{ fontWeight: 900, color: t.kind === 'error' ? 'var(--danger)' : t.kind === 'success' ? 'var(--success)' : 'var(--text)' }}>
              {t.kind === 'error' ? 'Error' : t.kind === 'success' ? 'Success' : 'Notice'}
            </div>
            <div style={{ marginTop: 6, color: 'var(--muted)', lineHeight: 1.5 }}>{t.message}</div>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastCtx)
  if (!ctx) throw new Error('ToastProvider missing')
  return ctx
}

